import React, { useEffect } from 'react'
import useFilterStore from '../../../Store/filterStore' // Adjust the path as needed
import styles from '../modal.module.css'
import Tooltip from '@mui/material/Tooltip'
import infoIcon from './images/circle-info-regular-full.svg'

function Amenities ({ amenitiesFilters, setAmenitiesFilters }) {
  const globalAmenities = useFilterStore(state => state.allFilters.amenities)

  useEffect(() => {
    // Update local state with global store values when component mounts
    setAmenitiesFilters(globalAmenities)
  }, [globalAmenities, setAmenitiesFilters])

  const handleAmenityChange = (event, amenity) => {
    const { name, value } = event.target
    // Allow only numeric input (or empty string for clearing)
    const re = /^[0-9\b]+$/
    if (re.test(value) || value === '') {
      setAmenitiesFilters(prevFilters => ({
        ...prevFilters,
        [amenity]: {
          ...prevFilters[amenity],
          [name]: value
        }
      }))
    }
  }

  const handleCheckboxChange = (e, category) => {
    const { name, checked } = e.target
    setAmenitiesFilters(prevFilters => {
      const newCategoryValues = new Set(prevFilters[category])
      if (checked) newCategoryValues.add(name)
      else newCategoryValues.delete(name)
      return { ...prevFilters, [category]: Array.from(newCategoryValues) }
    })
  }

  return (
    <section className={styles.amenitiesSection}>
      <div className={styles.sectionTitleWithIcon}>
        <h1 className={styles.amenitiesHeader}>Amenities</h1>
      </div>

      <div className={styles.amenitiesGroup}>
        {/* Beds */}
        <div className={styles.amenitiesColumn}>
          <div className={styles.sectionTitleWithIcon}>
            <label className={styles.labelsAmenities}>Beds</label>
            <Tooltip
              title='Filter by number of bedrooms. Enter a minimum and/or maximum.'
              arrow
              placement='top'
              slotProps={{
                popper: {
                  sx: {
                    '& .MuiTooltip-tooltip': {
                      maxWidth: 500,
                      fontSize: '13px',
                      padding: '8px'
                    }
                  }
                }
              }}
            >
              <img
                src={infoIcon}
                alt='Beds info'
                width={14}
                height={14}
                className={styles.infoIcon}
              />
            </Tooltip>
          </div>
          <div className={styles.amenitiesRow}>
            <input
              autoComplete='off'
              id='bedsMin'
              className={styles.inputAmenities}
              placeholder='Min'
              name='min'
              value={amenitiesFilters.beds.min}
              onChange={e => handleAmenityChange(e, 'beds')}
            />
            <input
              autoComplete='off'
              id='bedsMax'
              className={styles.inputAmenities}
              placeholder='Max'
              name='max'
              value={amenitiesFilters.beds.max}
              onChange={e => handleAmenityChange(e, 'beds')}
            />
          </div>
        </div>

        {/* Bath */}
        <div className={styles.amenitiesColumn}>
          <div className={styles.sectionTitleWithIcon}>
            <label className={styles.labelsAmenities}>Bath</label>
            <Tooltip
              title='Filter by number of bathrooms. Enter a minimum and/or maximum.'
              arrow
              placement='top'
              slotProps={{
                popper: {
                  sx: {
                    '& .MuiTooltip-tooltip': {
                      maxWidth: 500,
                      fontSize: '13px',
                      padding: '8px'
                    }
                  }
                }
              }}
            >
              <img
                src={infoIcon}
                alt='Bathrooms info'
                width={14}
                height={14}
                className={styles.infoIcon}
              />
            </Tooltip>
          </div>
          <div className={styles.amenitiesRow}>
            <input
              autoComplete='off'
              id='bathMin'
              className={styles.inputAmenities}
              placeholder='Min'
              name='min'
              value={amenitiesFilters.bath.min}
              onChange={e => handleAmenityChange(e, 'bath')}
            />
            <input
              autoComplete='off'
              id='bathMax'
              className={styles.inputAmenities}
              placeholder='Max'
              name='max'
              value={amenitiesFilters.bath.max}
              onChange={e => handleAmenityChange(e, 'bath')}
            />
          </div>
        </div>

        {/* Floors */}
        <div className={styles.amenitiesColumn}>
          <div className={styles.sectionTitleWithIcon}>
            <label className={styles.labelsAmenities}>Floors</label>
            <Tooltip
              title='Filter by floors/stories. Enter a minimum and/or maximum.'
              arrow
              placement='top'
              slotProps={{
                popper: {
                  sx: {
                    '& .MuiTooltip-tooltip': {
                      maxWidth: 500,
                      fontSize: '13px',
                      padding: '8px'
                    }
                  }
                }
              }}
            >
              <img
                src={infoIcon}
                alt='Floors info'
                width={14}
                height={14}
                className={styles.infoIcon}
              />
            </Tooltip>
          </div>
          <div className={styles.amenitiesRow}>
            <input
              autoComplete='off'
              id='floorsMin'
              className={styles.inputAmenities}
              placeholder='Min'
              name='min'
              value={amenitiesFilters.floors.min}
              onChange={e => handleAmenityChange(e, 'floors')}
            />
            <input
              autoComplete='off'
              id='floorsMax'
              className={styles.inputAmenities}
              placeholder='Max'
              name='max'
              value={amenitiesFilters.floors.max}
              onChange={e => handleAmenityChange(e, 'floors')}
            />
          </div>
        </div>

        {/* Parking spaces */}
        <div className={styles.amenitiesColumn}>
          <div className={styles.sectionTitleWithIcon}>
            <label className={styles.labelsAmenities}>Parking spaces</label>
            <Tooltip
              title='Filter by on-property parking spaces. Enter a minimum and/or maximum.'
              arrow
              placement='top'
              slotProps={{
                popper: {
                  sx: {
                    '& .MuiTooltip-tooltip': {
                      maxWidth: 500,
                      fontSize: '13px',
                      padding: '8px'
                    }
                  }
                }
              }}
            >
              <img
                src={infoIcon}
                alt='Parking info'
                width={14}
                height={14}
                className={styles.infoIcon}
              />
            </Tooltip>
          </div>
          <div className={styles.amenitiesRow}>
            <input
              autoComplete='off'
              id='parkingMin'
              className={styles.inputAmenities}
              placeholder='Min'
              name='min'
              value={amenitiesFilters.parkingSpaces.min}
              onChange={e => handleAmenityChange(e, 'parkingSpaces')}
            />
            <input
              autoComplete='off'
              id='parkingMax'
              className={styles.inputAmenities}
              placeholder='Max'
              name='max'
              value={amenitiesFilters.parkingSpaces.max}
              onChange={e => handleAmenityChange(e, 'parkingSpaces')}
            />
          </div>
        </div>

        {/* Rooms */}
        <div className={styles.amenitiesColumn}>
          <div className={styles.sectionTitleWithIcon}>
            <label className={styles.labelsAmenities}>Rooms</label>
            <Tooltip
              title='Filter by total number of rooms. Enter a minimum and/or maximum.'
              arrow
              placement='top'
              slotProps={{
                popper: {
                  sx: {
                    '& .MuiTooltip-tooltip': {
                      maxWidth: 500,
                      fontSize: '13px',
                      padding: '8px'
                    }
                  }
                }
              }}
            >
              <img
                src={infoIcon}
                alt='Rooms info'
                width={14}
                height={14}
                className={styles.infoIcon}
              />
            </Tooltip>
          </div>
          <div className={styles.amenitiesRow}>
            <input
              autoComplete='off'
              id='roomsMin'
              className={styles.inputAmenities}
              placeholder='Min'
              name='min'
              value={amenitiesFilters.rooms.min}
              onChange={e => handleAmenityChange(e, 'rooms')}
            />
            <input
              autoComplete='off'
              id='roomsMax'
              className={styles.inputAmenities}
              placeholder='Max'
              name='max'
              value={amenitiesFilters.rooms.max}
              onChange={e => handleAmenityChange(e, 'rooms')}
            />
          </div>
        </div>

        {/* Units */}
        <div className={styles.amenitiesColumn}>
          <div className={styles.sectionTitleWithIcon}>
            <label className={styles.labelsAmenities}>Units</label>
            <Tooltip
              title='Filter by number of units. Enter a minimum and/or maximum.'
              arrow
              placement='top'
              slotProps={{
                popper: {
                  sx: {
                    '& .MuiTooltip-tooltip': {
                      maxWidth: 500,
                      fontSize: '13px',
                      padding: '8px'
                    }
                  }
                }
              }}
            >
              <img
                src={infoIcon}
                alt='Units info'
                width={14}
                height={14}
                className={styles.infoIcon}
              />
            </Tooltip>
          </div>
          <div className={styles.amenitiesRow}>
            <input
              autoComplete='off'
              id='unitsMin'
              className={styles.inputAmenities}
              placeholder='Min'
              name='min'
              value={amenitiesFilters.units.min}
              onChange={e => handleAmenityChange(e, 'units')}
            />
            <input
              autoComplete='off'
              id='unitsMax'
              className={styles.inputAmenities}
              placeholder='Max'
              name='max'
              value={amenitiesFilters.units.max}
              onChange={e => handleAmenityChange(e, 'units')}
            />
          </div>
        </div>
      </div>

      {/* Special amenities */}
      <section className={styles.specialAmenitiesRowContainer}>
        {/* HVAC */}
        <div className={styles.amenitiesColumn}>
          <div className={styles.sectionTitleWithIcon}>
            <label className={styles.titleSpecialAmenities}>HVAC</label>
            <Tooltip
              title='Filter by HVAC types.'
              arrow
              placement='top'
              slotProps={{
                popper: {
                  sx: {
                    '& .MuiTooltip-tooltip': {
                      maxWidth: 500,
                      fontSize: '13px',
                      padding: '8px'
                    }
                  }
                }
              }}
            >
              <img
                src={infoIcon}
                alt='HVAC info'
                width={14}
                height={14}
                className={styles.infoIcon}
              />
            </Tooltip>
          </div>

          <div className={styles.specialAmenitiesRow}>
            <input
              checked={amenitiesFilters.HVAC.includes('Central Air')}
              onChange={e => handleCheckboxChange(e, 'HVAC')}
              type='checkbox'
              id='Central Air'
              name='Central Air'
              className={styles.specialAmenitiesInput}
            />
            <label className={styles.specialAmenitiesLabel}>Central Air</label>
          </div>
          <div className={styles.specialAmenitiesRow}>
            <input
              className={styles.specialAmenitiesInput}
              checked={amenitiesFilters.HVAC.includes('Electric')}
              onChange={e => handleCheckboxChange(e, 'HVAC')}
              type='checkbox'
              id='Electric'
              name='Electric'
            />
            <label className={styles.specialAmenitiesLabel}>Electric</label>
          </div>
          <div className={styles.specialAmenitiesRow}>
            <input
              className={styles.specialAmenitiesInput}
              checked={amenitiesFilters.HVAC.includes('Fans')}
              onChange={e => handleCheckboxChange(e, 'HVAC')}
              type='checkbox'
              id='Fans'
              name='Fans'
            />
            <label className={styles.specialAmenitiesLabel}>Fans</label>
          </div>
        </div>

        {/* Exterior */}
        <div className={styles.amenitiesColumn}>
          <div className={styles.sectionTitleWithIcon}>
            <label className={styles.titleSpecialAmenities}>Exterior</label>
            <Tooltip
              title='Filter by exterior features.'
              arrow
              placement='top'
              slotProps={{
                popper: {
                  sx: {
                    '& .MuiTooltip-tooltip': {
                      maxWidth: 500,
                      fontSize: '13px',
                      padding: '8px'
                    }
                  }
                }
              }}
            >
              <img
                src={infoIcon}
                alt='Exterior info'
                width={14}
                height={14}
                className={styles.infoIcon}
              />
            </Tooltip>
          </div>

          <div className={styles.specialAmenitiesRow}>
            <input
              type='checkbox'
              id='Pool'
              checked={amenitiesFilters.exterior.includes('Pool')}
              onChange={e => handleCheckboxChange(e, 'exterior')}
              name='Pool'
              className={styles.specialAmenitiesInput}
            />
            <label className={styles.specialAmenitiesLabel}>Pool</label>
          </div>
          <div className={styles.specialAmenitiesRow}>
            <input
              type='checkbox'
              id='Breezeway'
              checked={amenitiesFilters.exterior.includes('Breezeway')}
              onChange={e => handleCheckboxChange(e, 'exterior')}
              name='Breezeway'
              className={styles.specialAmenitiesInput}
            />
            <label className={styles.specialAmenitiesLabel}>Breezeway</label>
          </div>
          <div className={styles.specialAmenitiesRow}>
            <input
              type='checkbox'
              id='Storm Shutters'
              checked={amenitiesFilters.exterior.includes('Storm Shutters')}
              onChange={e => handleCheckboxChange(e, 'exterior')}
              name='Storm Shutters'
              className={styles.specialAmenitiesInput}
            />
            <label className={styles.specialAmenitiesLabel}>
              Storm Shutters
            </label>
          </div>
        </div>

        {/* Roofing */}
        <div className={styles.amenitiesColumn}>
          <div className={styles.sectionTitleWithIcon}>
            <label className={styles.titleSpecialAmenities}>Roofing</label>
            <Tooltip
              title='Filter by roofing materials.'
              arrow
              placement='top'
              slotProps={{
                popper: {
                  sx: {
                    '& .MuiTooltip-tooltip': {
                      maxWidth: 500,
                      fontSize: '13px',
                      padding: '8px'
                    }
                  }
                }
              }}
            >
              <img
                src={infoIcon}
                alt='Roofing info'
                width={14}
                height={14}
                className={styles.infoIcon}
              />
            </Tooltip>
          </div>

          <div className={styles.specialAmenitiesRow}>
            <input
              type='checkbox'
              id='Asphalt'
              name='Asphalt'
              checked={amenitiesFilters.roofing.includes('Asphalt')}
              onChange={e => handleCheckboxChange(e, 'roofing')}
              className={styles.specialAmenitiesInput}
            />
            <label className={styles.specialAmenitiesLabel}>Asphalt</label>
          </div>
          <div className={styles.specialAmenitiesRow}>
            <input
              type='checkbox'
              id='Fiberglass'
              name='Fiberglass'
              checked={amenitiesFilters.roofing.includes('Fiberglass')}
              onChange={e => handleCheckboxChange(e, 'roofing')}
              className={styles.specialAmenitiesInput}
            />
            <label className={styles.specialAmenitiesLabel}>Fiberglass</label>
          </div>
        </div>
      </section>
    </section>
  )
}

export default Amenities
